import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskPaymentMethodPickerBlockConfig } from '@front-platform/content-builder';
import type { TranslationField } from '@shared/interfaces/translation-field.interface';
import {
  KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS,
  kioskPaymentMethodPickerBlockResolveConfig,
  resolveKioskPaymentMethodPickerDisplayedText,
  resolveKioskPaymentMethodPickerText,
} from '@front-platform/content-builder';
import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { of, Subject } from 'rxjs';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { map, takeUntil, tap } from 'rxjs/operators';
import { DoodOrderModel } from '@store/order/order.model';
import {
  IPaymentMethod,
  KioskPaymentMethodCard,
  PaymentMethodHandles,
} from '@config/payment-methods.config';
import { OrderKeys } from '@config/keys/order.keys';
import {
  PAYMENT_STORE_BUTTON_INITIAL_STATE,
  PAYMENT_STORE_STATUS_INITIAL_STATE,
} from '@store/payment';
import { TerminalAbortService } from '@core/services/yavin-terminal/terminal-abort.service';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ErrorService } from '@core/services/error-service/error.service';
import { PaymentStoreRefiner } from '@common/refiners/payment.refiner';
import { PaymentStoreSelector } from '@common/selectors/payment.selector';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { PaymentService } from '@core/services/payment/payment.service';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { KioskPaymentMethodPickerStep } from '@shared/blocks/kiosk-payment-method-picker-block/kiosk-payment-method-picker-step-enum';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { Router } from '@angular/router';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import {
  KIOSK_PAYMENT_METHOD_PICKER_BLOCK_DATA_SOURCE,
  KioskPaymentMethodPickerBlockViewModel,
} from './kiosk-payment-method-picker-block-view-model';
import { provideKioskPaymentMethodPickerBlockDataSource } from './kiosk-payment-method-picker-block-data-source.providers';

export interface KioskPaymentMethodPickerBlockRuntimeConfig {
  'header'?: unknown[];
  'terminal_content'?: unknown[];
}

export type KioskPaymentMethodPickerBlockConfigInput = Partial<
  Omit<KioskPaymentMethodPickerBlockConfig, keyof KioskPaymentMethodPickerBlockRuntimeConfig> &
    KioskPaymentMethodPickerBlockRuntimeConfig
>;
export type KioskPaymentMethodPickerBlockResolvedConfig = Omit<
  KioskPaymentMethodPickerBlockConfig,
  keyof KioskPaymentMethodPickerBlockRuntimeConfig
> &
  Partial<KioskPaymentMethodPickerBlockRuntimeConfig>;
export interface KioskPaymentMethodPickerBlockContract extends ConfigComponentContract<
  KioskPaymentMethodPickerBlockConfigInput,
  KioskPaymentMethodPickerBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-payment-method-picker-block',
  templateUrl: './kiosk-payment-method-picker-block.component.html',
  styles: [],
  standalone: false,
  providers: [...provideKioskPaymentMethodPickerBlockDataSource()],
})
export class KioskPaymentMethodPickerBlockComponent
  implements OnInit, OnDestroy, KioskPaymentMethodPickerBlockContract
{
  readonly config = input<KioskPaymentMethodPickerBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskPaymentMethodPickerBlockConfig,
    KioskPaymentMethodPickerBlockRuntimeConfig
  >(kioskPaymentMethodPickerBlockResolveConfig, () => ({ config: this.config() }));
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly blockDataSource = inject(KIOSK_PAYMENT_METHOD_PICKER_BLOCK_DATA_SOURCE);
  private readonly previewContent = signal<KioskPaymentMethodPickerBlockViewModel | null>(null);

  readonly header = computed(() => this.resolvedConfig()['header'] as unknown as unknown[]);
  readonly terminalContent = computed(
    () => (this.resolvedConfig()['terminal_content'] as unknown[] | undefined) ?? [],
  );
  /** Custom blocks replace the default terminal page (image and text). */
  readonly hasCustomTerminalContent = computed(() => this.terminalContent().length > 0);
  readonly displayCancelButton = computed(
    () => this.resolvedConfig()['display_cancel_button'] !== false,
  );
  readonly cancelButtonLabel = computed(
    () => this.resolvedConfig()['cancel_button_label'] as unknown as TranslationField | undefined,
  );
  readonly cancelButtonBackgroundColor = computed(
    () =>
      this.resolvedConfig()[
        'cancel_button_background_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly cancelButtonTextColor = computed(
    () =>
      this.resolvedConfig()['cancel_button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly headingChoosePaymentMethod = computed(() =>
    resolveKioskPaymentMethodPickerDisplayedText(
      KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS.headingChoosePaymentMethod,
      this.resolvedConfig()['heading_choose_payment_method'],
      this.previewContent()?.headingChoosePaymentMethod,
    ),
  );
  readonly choicePayOnSite = computed(() =>
    resolveKioskPaymentMethodPickerText(
      KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS.choicePayOnSite,
      this.previewContent()?.choicePayOnSite,
      this.resolvedConfig()['choice_pay_on_site'],
    ),
  );
  readonly choicePayOnKiosk = computed(() =>
    resolveKioskPaymentMethodPickerText(
      KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS.choicePayOnKiosk,
      this.previewContent()?.choicePayOnKiosk,
      this.resolvedConfig()['choice_pay_on_kiosk'],
    ),
  );
  readonly payOnKioskText = computed(() =>
    resolveKioskPaymentMethodPickerText(
      KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS.payOnKioskText,
      this.previewContent()?.payOnKioskText,
      this.resolvedConfig()['pay_on_kiosk_text'],
    ),
  );
  readonly displayPayOnKioskImage = computed(
    () =>
      (this.previewContent()?.displayPayOnKioskImage ??
        this.resolvedConfig()['display_pay_on_kiosk_image']) as unknown as boolean,
  );
  readonly payOnKioskImage = computed(
    () =>
      (this.previewContent()?.payOnKioskImage ??
        this.resolvedConfig()['pay_on_kiosk_image']) as unknown as IContentBuilderFieldImage,
  );
  readonly terminalInstructionMessage = computed(() =>
    resolveKioskPaymentMethodPickerText(
      KIOSK_PAYMENT_METHOD_PICKER_FALLBACK_I18N_KEYS.terminalInstructionMessage,
      this.previewContent()?.terminalInstructionMessage,
      this.resolvedConfig()['terminal_instruction_message'],
    ),
  );
  readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardInactiveBackgroundColor = computed(
    () =>
      this.resolvedConfig()[
        'card_inactive_background_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly cardInactiveTextColor = computed(
    () => this.resolvedConfig()['card_inactive_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardActiveBackgroundColor = computed(
    () =>
      this.resolvedConfig()['card_active_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardActiveTextColor = computed(
    () => this.resolvedConfig()['card_active_text_color'] as unknown as IContentBuilderFieldColor,
  );

  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly errorService = inject(ErrorService);
  private readonly paymentRefiner = inject(PaymentStoreRefiner);
  private readonly paymentSelector = inject(PaymentStoreSelector);
  private readonly orderDispatcher = inject(OrderStoreDispatcher);
  private readonly paymentService = inject(PaymentService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly router = inject(Router);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly terminalAbortService = inject(TerminalAbortService);

  private readonly _destroyerRef = new Subject<boolean>();

  order$ = this.orderSelector.selectValidation$;
  availablePaymentMethods$ = this.paymentRefiner.selectPaymentMethodsWithDescriptors$;
  isPaymentRetryInProgress$ = this.paymentSelector.selectStatus.pipe(map(s => s.isRetryInProgress));
  activeOrderCreatedAt$ = this.order$.pipe(
    map(order => (order ? (order as unknown as DoodOrderModel).created_at : null)),
  );

  readonly orderKeys = OrderKeys;
  readonly kioskPaymentMethodPickerStep = KioskPaymentMethodPickerStep;

  protected errorMessages$ = this.errorService.getErrorMessage$();
  protected isOrderValidation$ = this.orderSelector.selectIsValidating$;

  availablePaymentMethods?: IPaymentMethod[];
  selectedPaymentMethod: IPaymentMethod | null = null;
  error: string | null = null;
  amount: number | null = null;
  button = PAYMENT_STORE_BUTTON_INITIAL_STATE;
  status = PAYMENT_STORE_STATUS_INITIAL_STATE;
  isCancelAvailable = true;
  isPayInProgress = false;
  isAbortingTerminal = false;
  abortTerminalFailed = false;

  protected paymentMethodCards: KioskPaymentMethodCard[] = [];
  protected selectedCard?: KioskPaymentMethodCard;
  protected step: KioskPaymentMethodPickerStep = KioskPaymentMethodPickerStep.CHOICE;
  protected displaySubmitButton = true;
  private readonly panelDataCache = new Map<string, object>();
  protected arePaymentsLoading = signal(true);
  protected hasSubmitAPayment = signal(false);
  protected hasSubmitAPaymentNextScreen = signal(false);
  protected isLoading = computed(
    () =>
      this.arePaymentsLoading() ||
      (this.hasSubmitAPayment() && !this.hasSubmitAPaymentNextScreen()),
  );
  protected readonly isPreviewMode = computed(() => this.previewState.isActive());

  constructor() {
    if (this.previewState.isActive()) {
      this.blockDataSource.vm$.pipe(takeUntil(this._destroyerRef)).subscribe(vm => {
        if (!vm) {
          return;
        }
        this.applyPreviewVm(vm);
      });
    }
  }

  private applyPreviewVm(vm: KioskPaymentMethodPickerBlockViewModel): void {
    this.previewContent.set(vm);
    this.panelDataCache.clear();
    this.order$ = of(vm.order as never) as typeof this.order$;
    this.availablePaymentMethods$ = of([...vm.availablePaymentMethods]);
    this.isPaymentRetryInProgress$ = of(false);
    this.activeOrderCreatedAt$ = this.order$.pipe(
      map(order => (order ? (order as unknown as DoodOrderModel).created_at : null)),
    );
    this.errorMessages$ = of([]);
    this.isOrderValidation$ = of(false);
    this.isCancelAvailable = true;
    this.isPayInProgress = false;
    this.isAbortingTerminal = false;
    this.abortTerminalFailed = false;
    this.selectedCard = undefined;
    this.step = vm.step;
    this.displaySubmitButton = vm.step === KioskPaymentMethodPickerStep.CHOICE;
    this.hasSubmitAPayment.set(false);
    this.hasSubmitAPaymentNextScreen.set(false);
    this.loadAvailablePaymentMethods();
  }

  ngOnInit(): void {
    if (this.previewState.isActive()) {
      return;
    }

    this.paymentSelector.select
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(({ amount, button, error, status }) => {
        this.status = status;
        this.button = button;
        this.amount = amount;
        this.error = error;
      });

    this.paymentDispatcher.resetUI();
    this.loadAvailablePaymentMethods();

    if (this.orderSelector.active) {
      this.orderDispatcher.resetActive();
    }
  }

  private loadAvailablePaymentMethods() {
    this.arePaymentsLoading.set(true);
    this.availablePaymentMethods$
      .pipe(
        tap(availablePaymentMethods => {
          availablePaymentMethods = availablePaymentMethods || [];
          availablePaymentMethods.forEach(a => {
            a.data = {
              ...a.data,
              isUnique: availablePaymentMethods.length === 1,
            };
          });

          this.availablePaymentMethods = availablePaymentMethods;

          this.paymentMethodCards = [];

          availablePaymentMethods.forEach(paymentMethod => {
            const isUsable = this.previewState.isActive()
              ? true
              : this.paymentService.isUsablePaymentMethod(paymentMethod);
            if (isUsable === false) {
              return;
            }

            if (
              paymentMethod.handle === PaymentMethodHandles.OnsiteWithoutDeposit ||
              paymentMethod.handle === PaymentMethodHandles.HouseAccount
            ) {
              this.paymentMethodCards.push({
                label: this.choicePayOnSite(),
                icon: 'icon-kiosk-icon-coins',
                paymentMethod: paymentMethod,
                hasNextScreen: false,
              });
            }

            if (
              [
                PaymentMethodHandles.YavinTerminal,
                PaymentMethodHandles.NeptingTerminal,
                PaymentMethodHandles.TcposSco,
                PaymentMethodHandles.AuresEtkTerminal,
                PaymentMethodHandles.StripeTerminal,
                PaymentMethodHandles.AdyenTerminal,
                PaymentMethodHandles.VivaWalletTerminal,
              ].includes(paymentMethod.handle)
            ) {
              this.paymentMethodCards.push({
                label: this.choicePayOnKiosk(),
                icon: 'icon-kiosk-icon-card',
                paymentMethod: paymentMethod,
                hasNextScreen: true,
              });
            }
          });
          this.arePaymentsLoading.set(false);

          if (
            this.paymentMethodCards.length === 1 &&
            this.paymentMethodCards[0].paymentMethod.handle !==
              PaymentMethodHandles.OnsiteWithoutDeposit &&
            this.paymentMethodCards[0].paymentMethod.handle !== PaymentMethodHandles.HouseAccount
          ) {
            this.selectedCard = this.paymentMethodCards[0];
            this.submit();
            return;
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  protected getPanelData(paymentMethod: IPaymentMethod): object {
    const message = this.terminalInstructionMessage();
    const cacheKey = `${paymentMethod.handle}:${message ?? ''}`;
    const cached = this.panelDataCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const base = paymentMethod.data ?? {};
    if (
      message != null &&
      (paymentMethod.handle === PaymentMethodHandles.YavinTerminal ||
        paymentMethod.handle === PaymentMethodHandles.NeptingTerminal ||
        paymentMethod.handle === PaymentMethodHandles.TcposSco ||
        paymentMethod.handle === PaymentMethodHandles.VivaWalletTerminal)
    ) {
      const panelData = { ...base, terminalInstructionMessage: message };
      this.panelDataCache.set(cacheKey, panelData);
      return panelData;
    }
    this.panelDataCache.set(cacheKey, base);
    return base;
  }

  protected selectCard(card: KioskPaymentMethodCard) {
    if (this.selectedCard === card) {
      this.selectedCard = undefined;
    } else {
      this.selectedCard = card;
    }
    // Wait a bit to let the UI update before submitting, otherwise the triggerPayment event will not be received by the payment component
    setTimeout(() => {
      this.submit();
    }, 0);
  }

  private submit() {
    if (!this.selectedCard) {
      return;
    }

    if (this.previewState.isActive()) {
      if (this.step === KioskPaymentMethodPickerStep.CHOICE && this.selectedCard.hasNextScreen) {
        this.step = KioskPaymentMethodPickerStep.TERMINAL;
        this.displaySubmitButton = false;
      }
      return;
    }

    this.hasSubmitAPayment.set(true);
    this.hasSubmitAPaymentNextScreen.set(this.selectedCard.hasNextScreen);

    if (this.step === KioskPaymentMethodPickerStep.CHOICE) {
      if (this.selectedCard.hasNextScreen) {
        this.step = KioskPaymentMethodPickerStep.TERMINAL;
        this.displaySubmitButton = false;
      } else {
        this.isCancelAvailable = false;
        this.isPayInProgress = true;
        this.paymentService.triggerPayment();
      }
      return;
    }

    if (this.step === KioskPaymentMethodPickerStep.TERMINAL) {
      this.paymentService.triggerPayment();
      return;
    }
  }

  protected back() {
    if (this.previewState.isActive()) {
      if (this.step === KioskPaymentMethodPickerStep.TERMINAL) {
        this.performBackToChoice();
      }
      return;
    }

    if (this.step === KioskPaymentMethodPickerStep.TERMINAL) {
      const abort$ = this.terminalAbortService.abortIfNeeded$();
      if (abort$) {
        this.isAbortingTerminal = true;
        this.abortTerminalFailed = false;
        abort$.pipe(takeUntil(this._destroyerRef)).subscribe(success => {
          if (success) {
            this.performBackToChoice();
          } else {
            this.abortTerminalFailed = true;
          }
        });
        return;
      }
      this.performBackToChoice();
      return;
    }

    if (this.step === KioskPaymentMethodPickerStep.CHOICE) {
      this.router.navigate([this.routerHelper.translateRoute('/cart')]);
      return;
    }
  }

  protected forceBackToChoice(): void {
    this.performBackToChoice();
  }

  private performBackToChoice(): void {
    this.isAbortingTerminal = false;
    this.abortTerminalFailed = false;
    this.selectedCard = undefined;
    this.step = KioskPaymentMethodPickerStep.CHOICE;
    this.displaySubmitButton = true;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskPaymentMethodPickerBlockComponent, {
  'keyAliases': {},
});
